import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Server, Database, FileUp, CheckCircle, XCircle, Loader2, Clock, PlusCircle, UploadCloud, FileText, RefreshCw, Trash2, LayoutGrid, List, CalendarPlus, Terminal, ChevronUp, ChevronDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import './AdminDashboard.css';
import authFetch from '../../utils/authFetch';

// HARDCODED FIX: Smartly switch between Localhost and Secure Server
const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const API_BASE = isLocal ? 'http://localhost:5000' : 'https://bdinteldb.com';

const ServerFileBrowser = ({ files, view, setView, onRefresh, onDelete, disabled, fmtDate }) => (
    <div className="server-file-browser">
        <div className="browser-header">
            <h5 className="browser-title">Files on Server ({files.length})</h5>
            <div className="browser-controls">
                <button onClick={() => setView('grid')} disabled={disabled} className={`view-btn ${view === 'grid' ? 'active' : 'inactive'}`}><LayoutGrid size={16} /></button>
                <button onClick={() => setView('list')} disabled={disabled} className={`view-btn ${view === 'list' ? 'active' : 'inactive'}`}><List size={16} /></button>
                <button onClick={onRefresh} disabled={disabled} className="refresh-btn"><RefreshCw size={14} color={disabled ? '#cbd5e1' : '#64748b'} className={disabled ? '' : 'hover-spin'} /></button>
            </div>
        </div>
        <div className="file-container">
            {files.length > 0 ? ( view === 'grid' ? (
                    <div className="file-grid">
                        {files.map(f => (
                            <div key={f.name} className="file-card">
                                <FileText size={18} color="#64748b" />
                                <span className="file-name" title={f.name}>{f.name}</span>
                                <button onClick={() => onDelete(f.name)} disabled={disabled} className="delete-btn"><Trash2 size={14} className="hover-danger" /></button>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="file-list">
                        {files.map(f => (
                            <div key={f.name} className="file-list-item">
                                <FileText size={14} color="#64748b" />
                                <div className="file-details">
                                    <span className="file-list-name" title={f.name}>{f.name}</span>
                                    <span className="file-modified-date">{fmtDate(f.modified)}</span>
                                </div>
                                <button onClick={() => onDelete(f.name)} disabled={disabled} className="delete-btn"><Trash2 size={14} className="hover-danger" /></button>
                            </div>
                        ))}
                    </div>
                )
            ) : (<div className="no-files">No files found.</div>)}
        </div>
    </div>
);

const FileUploadControl = ({ type, files, setFiles, serverFiles, fileView, setFileView, onUpload, onRunImport, onSchedule, onRefresh, onDelete, loading, disabled, fmtDate, lastRunStatus, onFolderSelect, onRunImportKn, showSchedule = true }) => {
    const inputRef = useRef(null);
    const folderInputRef = useRef(null); // New ref for folder input
    const [isDragging, setIsDragging] = useState(false);
    const fileType = type === 'ETN' ? '.csv' : '.pdf';
    const hasFiles = files && files.length > 0;
    const handleDrag = (e) => { e.preventDefault(); e.stopPropagation(); };
    const handleDragIn = (e) => { handleDrag(e); if (e.dataTransfer.items && e.dataTransfer.items.length > 0) setIsDragging(true); };
    const handleDragOut = (e) => { handleDrag(e); setIsDragging(false); };
    const addFiles = useCallback((newFiles) => {
        setFiles(prevFiles => {
            const prevFileNames = new Set(prevFiles.map(f => f.name));
            const uniqueNewFiles = newFiles.filter(file => !prevFileNames.has(file.name));

            if (uniqueNewFiles.length < newFiles.length) {
                const duplicateCount = newFiles.length - uniqueNewFiles.length;
                alert(`${duplicateCount} of the selected files were already in the list and have been ignored.`);
            }

            return [...prevFiles, ...uniqueNewFiles];
        });
    }, [setFiles]);

    const handleDrop = useCallback((e) => {
        handleDrag(e);
        setIsDragging(false);
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            const allDroppedFiles = Array.from(e.dataTransfer.files);
            const validFiles = allDroppedFiles.filter(file => file.name.toLowerCase().endsWith(fileType));
            
            if (validFiles.length < allDroppedFiles.length) {
                alert(`Only ${fileType} files are allowed. Invalid files were ignored.`);
            }

            if (validFiles.length > 0) {
                addFiles(validFiles);
            } else {
                alert(`No valid ${fileType} files were dropped.`);
            }
            e.dataTransfer.clearData();
        }
    }, [addFiles, fileType]);
    
    const handleFileChange = useCallback((e) => {
        if (e.target.files) {
            addFiles(Array.from(e.target.files));
        }
    }, [addFiles]);

    // New handler for folder selection
    const handleFolderChange = useCallback((e) => {
        if (e.target.files && e.target.files.length > 0) {
            onFolderSelect(Array.from(e.target.files));
        }
    }, [onFolderSelect]);

    return (
        <div className={`upload-box ${isDragging ? 'dragging' : ''}`} onDragEnter={handleDragIn} onDragLeave={handleDragOut} onDragOver={handleDrag} onDrop={handleDrop}>
            {isDragging && (
                <div className="drag-overlay">
                    <UploadCloud size={32} color="#2563eb" />
                    <span className="drag-overlay-content">Drop to Upload</span>
                </div>
            )}
            <div className="upload-box-title">{`${type} Data`}</div>
            <button onClick={() => inputRef.current.click()} className="btn btn-secondary" style={{width: '100%', marginBottom: '10px'}} disabled={disabled}>
                <UploadCloud size={16} />{hasFiles ? `${files.length} file(s) selected` : 'Select Files or Drop Here'}
            </button>
            <input type="file" multiple accept={fileType} ref={inputRef} style={{ display: 'none' }} onChange={handleFileChange} />
            {type === 'ZK' && (
                <button onClick={() => folderInputRef.current.click()} className="btn btn-secondary" style={{ width: '100%', marginBottom: '10px' }} disabled={disabled}>
                    <UploadCloud size={16} /> Upload from folder
                </button>
            )}
            <input type="file" webkitdirectory="" directory="" ref={folderInputRef} style={{ display: 'none' }} onChange={handleFolderChange} />
                        <div className="btn-group">
                                <button onClick={onUpload} disabled={!hasFiles || loading || disabled} className={`btn ${hasFiles ? 'btn-primary' : ''}`}><FileUp size={16} />Upload</button>
                                {type === 'KN' ? (
                                    <>
                                        <button onClick={() => onRunImportKn && onRunImportKn('delistavb')} disabled={loading || disabled} className="btn btn-secondary"><Database size={16} />Import Deli stavb</button>
                                        <button onClick={() => onRunImportKn && onRunImportKn('imetniki')} disabled={loading || disabled} className="btn btn-secondary"><Database size={16} />Import Imetniki</button>
                                        <button onClick={() => onRunImportKn && onRunImportKn('osebe')} disabled={loading || disabled} className="btn btn-secondary"><Database size={16} />Import Osebe</button>
                                        <button onClick={() => onRunImportKn && onRunImportKn('naslovi')} disabled={loading || disabled} className="btn btn-secondary"><Database size={16} />Import Naslovi HS</button>
                                    </>
                                ) : (
                                    <>
                                        <button onClick={onRunImport} disabled={loading || disabled} className="btn btn-secondary"><Database size={16} />Run Import</button>
                                        {showSchedule && (
                                            <button onClick={onSchedule} disabled={loading || disabled} className="btn btn-secondary" style={{ flex: '0.5' }}><CalendarPlus size={16} /></button>
                                        )}
                                    </>
                                )}
                        </div>
            <ServerFileBrowser files={serverFiles} view={fileView} setView={setFileView} onRefresh={onRefresh} onDelete={onDelete} disabled={disabled || loading} fmtDate={fmtDate} />
            {lastRunStatus && <div className="last-run-status-container">{lastRunStatus}</div>}
        </div>
    );
};

const ScheduleModal = ({ isOpen, onClose, onSchedule, type, scheduleTime, setScheduleTime, loading }) => {
    if (!isOpen) return null;
    return (
        <div className="modal-overlay">
            <div className="modal-content">
                <h4 className="modal-title">Schedule Import: {type.toUpperCase()}</h4>
                <p className="modal-description">Select a future date and time to run the import.</p>
                <input type="datetime-local" value={scheduleTime} onChange={e => setScheduleTime(e.target.value)} className="modal-input" min={new Date().toISOString().slice(0, 16)} />
                <div className="modal-actions">
                    <button onClick={onClose} className="btn btn-secondary">Cancel</button>
                    <button onClick={onSchedule} disabled={!scheduleTime || loading} className={`btn ${scheduleTime ? 'btn-primary' : ''}`}>
                        {loading ? <Loader2 size={16} className="spin" /> : 'Schedule'}
                    </button>
                </div>
            </div>
        </div>
    );
};

const ScheduledJobsList = ({ jobs, onCancel, disabled, fmtDate }) => (
    <div className="scheduled-jobs-list">
        <h4 className="scheduled-jobs-title">Scheduled Imports</h4>
        {jobs.length > 0 ? (
            <div className="job-list">
                {jobs.map(job => (
                    <div key={job.jobId} className="job-item">
                        <CalendarPlus size={14} color="#64748b" />
                        <span className={`job-type ${job.type}`}>{job.type.toUpperCase()}</span>
                        <span className="job-time">{fmtDate(job.scheduleTime)}</span>
                        <button onClick={() => onCancel(job.jobId)} disabled={disabled} className="job-cancel-btn"><Trash2 size={14} className="hover-danger" /></button>
                    </div>
                ))}
            </div>
        ) : (
            <div className="no-jobs">No imports scheduled.</div>
        )}
    </div>
);

const LogViewer = () => {
    const [logs, setLogs] = useState([]);
    const [isOpen, setIsOpen] = useState(true);
    const logsEndRef = useRef(null);
    const eventSourceRef = useRef(null);
    const reconnectDelayRef = useRef(1000); // Start with 1s delay

    const connect = useCallback(() => {
        if (eventSourceRef.current) {
            eventSourceRef.current.close();
        }

        const token = localStorage.getItem('authToken') || '';
        const eventSource = new EventSource(`${API_BASE}/api/admin/logs?token=${encodeURIComponent(token)}`);
        eventSourceRef.current = eventSource;

        eventSource.onopen = () => {
            setLogs(prev => [{ type: 'system', message: 'Log stream connection established.', timestamp: new Date().toLocaleTimeString('sl-SI') }]);
            reconnectDelayRef.current = 1000; // Reset delay on successful connection
        };

        eventSource.onmessage = (event) => {
            try {
                const newLog = JSON.parse(event.data);
                newLog.timestamp = new Date().toLocaleTimeString('sl-SI');

                if (newLog.message && newLog.message.trim() === '.') {
                    setLogs(prev => {
                        if (prev.length === 0) return [newLog];
                        const last = prev[prev.length - 1];
                        if (last.message.includes('Importing') || last.message.endsWith('.')) {
                            return [...prev.slice(0, -1), { ...last, message: last.message + '.' }];
                        }
                        return [...prev, newLog];
                    });
                } else {
                    setLogs(prev => [...prev, newLog]);
                }
            } catch (e) {
                console.error("Failed to parse log data:", event.data);
            }
        };

        eventSource.onerror = () => {
            setLogs(prev => [...prev, { type: 'system', message: `Log stream connection error. Reconnecting in ${reconnectDelayRef.current / 1000}s...`, timestamp: new Date().toLocaleTimeString('sl-SI') }]);
            eventSource.close();

            // Exponential backoff
            setTimeout(() => {
                reconnectDelayRef.current = Math.min(reconnectDelayRef.current * 2, 30000); // Double the delay, max 30s
                connect();
            }, reconnectDelayRef.current);
        };
    }, []);

    useEffect(() => {
        connect();
        return () => {
            if (eventSourceRef.current) {
                eventSourceRef.current.close();
            }
        };
    }, [connect]);

    useEffect(() => {
        if (isOpen) {
            logsEndRef.current?.scrollIntoView({ behavior: "smooth" });
        }
    }, [logs, isOpen]);

    const renderLog = (log, index) => {
        let color = '#cbd5e1'; // slate-300 for stdout
        if (log.type === 'stderr') color = '#f87171'; // red-400
        if (log.type === 'system') color = '#38bdf8'; // sky-400
        
        const timestamp = log.timestamp || new Date().toLocaleTimeString('sl-SI');

        return (
            <div key={index} className="log-line">
                <span className="log-timestamp">{`[${timestamp}]`}</span>
                <span style={{ color }}>{log.message}</span>
            </div>
        );
    };

    return (
        <div className="log-viewer">
            <div className="log-header" onClick={() => setIsOpen(!isOpen)}>
                <h4 className="log-title">
                    <Terminal size={14} /> Live Import Logs
                </h4>
                <button className="log-toggle-btn">
                    {isOpen ? <ChevronUp size={18} color="#64748b" /> : <ChevronDown size={18} color="#64748b" />}
                </button>
            </div>
            {isOpen && (
                <div className="log-content">
                    {logs.map(renderLog)}
                    <div ref={logsEndRef} />
                </div>
            )}
        </div>
    );
};

const readResponsePayload = async (response) => {
    const contentType = response.headers.get('content-type') || '';
    if (contentType.includes('application/json')) {
        return response.json();
    }

    const text = await response.text();
    return {
        error: text.startsWith('<!DOCTYPE') ? 'Server returned an HTML error page.' : text
    };
};


const AdminDashboard = () => {
    const [status, setStatus] = useState('offline');
    const [jobs, setJobs] = useState({});
    const [scheduledJobs, setScheduledJobs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [syncAddedItems, setSyncAddedItems] = useState([]);
    const [etnFiles, setEtnFiles] = useState([]);
    const [zkFiles, setZkFiles] = useState([]);
    const [knFiles, setKnFiles] = useState([]);
    const [knStavbeFiles, setKnStavbeFiles] = useState([]);
    const [evFiles, setEvFiles] = useState([]);
    const [etnServerFiles, setEtnServerFiles] = useState([]);
    const [zkServerFiles, setZkServerFiles] = useState([]);
    const [knServerFiles, setKnServerFiles] = useState([]);
    const [knStavbeServerFiles, setKnStavbeServerFiles] = useState([]);
    const [evServerFiles, setEvServerFiles] = useState([]);
    const [etnFileView, setEtnFileView] = useState('list');
    const [zkFileView, setZkFileView] = useState('list');
    const [knFileView, setKnFileView] = useState('list');
    const [knStavbeFileView, setKnStavbeFileView] = useState('list');
    const [evFileView, setEvFileView] = useState('list');
    const [isScheduleModalOpen, setScheduleModalOpen] = useState(false);
    const [scheduleType, setScheduleType] = useState('');
    const [scheduleTime, setScheduleTime] = useState('');
    const navigate = useNavigate();

    const refreshData = useCallback(() => {
        if (loading) return;
        authFetch(`${API_BASE}/api/admin/status`).then(res => res.ok ? setStatus('online') : setStatus('offline')).catch(() => setStatus('offline'));
        authFetch(`${API_BASE}/api/admin/jobs`).then(res => res.json()).then(data => setJobs(data)).catch(console.error);
        authFetch(`${API_BASE}/api/admin/scheduled-jobs`).then(res => res.json()).then(data => setScheduledJobs(data)).catch(console.error);
        authFetch(`${API_BASE}/api/admin/list-files/etn`).then(res => res.json()).then(data => setEtnServerFiles(data)).catch(console.error);
        authFetch(`${API_BASE}/api/admin/list-files/zk`).then(res => res.json()).then(data => setZkServerFiles(data)).catch(console.error);
        authFetch(`${API_BASE}/api/admin/list-files/kn`).then(res => res.json()).then(data => setKnServerFiles(data)).catch(console.error);
        authFetch(`${API_BASE}/api/admin/list-files/knstavbe`).then(res => res.json()).then(data => setKnStavbeServerFiles(data)).catch(console.error);
        authFetch(`${API_BASE}/api/admin/list-files/ev`).then(res => res.json()).then(data => setEvServerFiles(data)).catch(console.error);
    }, [loading]);

    useEffect(() => { 
        refreshData(); 
        const interval = setInterval(refreshData, 10000); 
        return () => {
            clearInterval(interval);
        };
    }, [refreshData]);

    const openScheduleModal = (type) => { setScheduleType(type); setScheduleTime(new Date(Date.now() + 60 * 60 * 1000).toISOString().slice(0, 16)); setScheduleModalOpen(true); };
    const closeScheduleModal = () => { setScheduleModalOpen(false); setScheduleTime(''); setScheduleType(''); };

    const handleScheduleImport = async () => {
        setLoading(true);
        try {
            const res = await authFetch(`${API_BASE}/api/admin/schedule-import`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ type: scheduleType, scheduleTime }) });
            const result = await res.json();
            if (!res.ok) throw new Error(result.error || 'Failed to schedule');
            setMessage(result.message);
        } catch (err) { setMessage(`Error: ${err.message}`);
        } finally { setLoading(false); closeScheduleModal(); setTimeout(refreshData, 500); }
    };
    
    const handleCancelJob = async (jobId) => {
        if (!window.confirm(`Cancel this scheduled import?`)) return;
        setLoading(true);
        try {
            const res = await authFetch(`${API_BASE}/api/admin/cancel-job/${jobId}`, { method: 'DELETE' });
            const result = await res.json();
            if (!res.ok) throw new Error(result.error || 'Failed to cancel');
            setMessage(result.message);
        } catch (err) { setMessage(`Error: ${err.message}`);
        } finally { setLoading(false); setTimeout(refreshData, 500); }
    };

    const handleDeleteFile = async (type, filename) => {
        if (!window.confirm(`Delete '${filename}'? This cannot be undone.`)) return;
        setLoading(true); setMessage(`Deleting ${filename}...`);
        try {
            const res = await authFetch(`${API_BASE}/api/admin/delete-file/${type}/${encodeURIComponent(filename)}`, { method: 'DELETE' });
            const result = await res.json();
            if (!res.ok) throw new Error(result.error || 'Failed to delete');
            setMessage(result.message);
        } catch (err) { setMessage(`Error: ${err.message}`);
        } finally { setLoading(false); setTimeout(refreshData, 500); }
    };

    const handleClearZK = async () => {
        if (!window.confirm('⚠️ WARNING: This will permanently delete ALL ZK entries from the database!\n\nThis action cannot be undone. Are you absolutely sure?')) return;
        
        // Double confirmation
        const confirmation = window.prompt('Type "DELETE ALL ZK" to confirm:');
        if (confirmation !== 'DELETE ALL ZK') {
            setMessage('ZK deletion cancelled.');
            return;
        }

        setLoading(true); 
        setMessage('Deleting all ZK entries...');
        try {
            const res = await authFetch(`${API_BASE}/api/admin/clear-zk`, { method: 'DELETE' });
            const result = await res.json();
            if (!res.ok) throw new Error(result.error || 'Failed to clear ZK table');
            setMessage(`✅ ${result.message} (${result.deletedCount} entries deleted)`);
        } catch (err) { 
            setMessage(`❌ Error: ${err.message}`);
        } finally { 
            setLoading(false); 
            setTimeout(refreshData, 500); 
        }
    };

    const handleSyncContacts = async () => {
        if (!window.confirm('Sync contacts from the whole app now? This will pull owners, cold-calling leads, showings, and aggregated ZK owners into Contacts.')) return;
        setLoading(true);
        setMessage('Syncing contacts from the whole app...');
        setSyncAddedItems([]);
        try {
            const res = await authFetch(`${API_BASE}/api/admin/contacts/sync-all`, { method: 'POST' });
            const result = await readResponsePayload(res);
            if (!res.ok) throw new Error(result.details || result.error || 'Failed to sync contacts');
            setMessage(`${result.message} Owners: ${result.stats.ownersProcessed}, Cold calls: ${result.stats.coldCallingProcessed}, Showings: ${result.stats.showingsProcessed}, ZK owners: ${result.stats.zkOwnerCount}`);
            setSyncAddedItems(Array.isArray(result.stats?.addedItems) ? result.stats.addedItems : []);
        } catch (err) {
            setMessage(`Error: ${err.message}`);
            setSyncAddedItems([]);
        } finally {
            setLoading(false);
        }
    };

    const handleUpload = async (type, filesToUpload = null) => {
        const files = filesToUpload || (type === 'etn' ? etnFiles : type === 'zk' ? zkFiles : type === 'kn' ? knFiles : type === 'knstavbe' ? knStavbeFiles : evFiles);
        if (files.length === 0) {
            setMessage('No files selected for upload.');
            return;
        }

        const serverFiles = type === 'etn' ? etnServerFiles : type === 'zk' ? zkServerFiles : type === 'kn' ? knServerFiles : type === 'knstavbe' ? knStavbeServerFiles : evServerFiles;
        const serverFileLookup = new Set(serverFiles.map(f => `${f.name}_${new Date(f.modified).getTime()}`));

        const uniqueFilesForUpload = [];
        const skippedFiles = [];

        files.forEach(file => {
            const fileIdentity = `${file.name}_${file.lastModified}`;
            if (serverFileLookup.has(fileIdentity)) {
                skippedFiles.push(file.name);
            } else {
                uniqueFilesForUpload.push(file);
            }
        });

        if (uniqueFilesForUpload.length === 0) {
            let skipMessage = 'All selected files are already on the server.';
            if (skippedFiles.length > 0) {
                skipMessage += ` Skipped: ${skippedFiles.join(', ')}`;
            }
            setMessage(skipMessage);
            return;
        }

        setLoading(true);
        let uploadMessage = `Uploading ${uniqueFilesForUpload.length} file(s)...`;
        if (skippedFiles.length > 0) {
            uploadMessage += ` (Skipped ${skippedFiles.length} duplicate file(s): ${skippedFiles.join(', ')})`;
        }
        setMessage(uploadMessage);

        const formData = new FormData();
        uniqueFilesForUpload.forEach(file => { formData.append('files', file); });
        try {
            const res = await authFetch(`${API_BASE}/api/admin/upload/${type}`, { method: 'POST', body: formData });
            
            if (!res.ok) {
                const errorText = await res.text();
                throw new Error(`Server Error (${res.status}): ${errorText.substring(0, 200)}...`);
            }

            const result = await res.json();
            if (!res.ok) throw new Error(result.error || 'Upload failed');
            setMessage(result.message);
            if (type === 'etn') setEtnFiles([]); else if (type === 'zk') setZkFiles([]); else if (type === 'kn') setKnFiles([]); else if (type === 'knstavbe') setKnStavbeFiles([]); else setEvFiles([]);
        } catch (err) { 
            setMessage(`Upload Failed: ${err.message}`);
            console.error("Full upload error:", err);
        } finally { 
            setLoading(false); 
            setTimeout(refreshData, 1000); 
        }
    };

    const runScript = async (type, knTable = null) => {
        const confirmLabel = type === 'kn' && knTable ? `Start the import for KN (${knTable}) data?` : `Start the import for ${type.toUpperCase()} data?`;
        if (!window.confirm(confirmLabel)) return;
        setLoading(true); setMessage(`Starting ${type.toUpperCase()} import${knTable ? ` (${knTable})` : ''}...`);
        try {
            await authFetch(`${API_BASE}/api/admin/run-script`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ script: type, knTable }) });
            setMessage('Import process started.');
        } catch (err) { setMessage(`Error: ${err.message}`);
        } finally { setLoading(false); setTimeout(refreshData, 2000); }
    };

    const fmtDate = (iso) => iso ? new Date(iso).toLocaleString('sl-SI', { day:'2-digit', month:'2-digit', year:'numeric', hour:'2-digit', minute:'2-digit' }) : 'Never';

    const handleFolderSelectForZK = (selectedFiles) => {
        const prefix = "03-001_IDZNAK_";
        const filteredFiles = selectedFiles.filter(file => file.name.startsWith(prefix));

        if (filteredFiles.length === 0) {
            alert(`No files starting with "${prefix}" were found in the selected folder.`);
            setMessage(`No files starting with "${prefix}" were found in the selected folder.`); // Also set message in UI
            return;
        }

        setZkFiles(filteredFiles); // Update the state
        handleUpload('zk', filteredFiles); // Automatically trigger upload
    };

    return (
        <div className="admin-dashboard">
            <style>{`
                @media (max-width: 900px) {
                    .controls-grid {
                        display: flex !important;
                        flex-direction: column;
                    }
                    .upload-box {
                        width: 100% !important;
                        min-width: 0 !important;
                        box-sizing: border-box !important;
                    }
                }
            `}</style>
            <h3 className="dashboard-header"><Server /> System Control</h3>
            <div style={{ marginBottom: '16px' }}>
                <button
                    onClick={() => navigate('/admin/db-backup')}
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px',
                        backgroundColor: '#0f766e',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '8px 14px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        marginTop: '8px'
                    }}
                >
                    <Database size={16} /> Open Database Backup Page
                </button>
                <button
                    onClick={() => navigate('/admin/users')}
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px',
                        backgroundColor: '#475569',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '8px 14px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        marginTop: '8px',
                        marginLeft: '8px'
                    }}
                >
                    <Server size={16} /> Open User Management
                </button>
                <button
                    onClick={handleSyncContacts}
                    disabled={loading || status === 'offline'}
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px',
                        backgroundColor: '#1d4ed8',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '8px 14px',
                        cursor: loading || status === 'offline' ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        marginTop: '8px',
                        marginLeft: '8px',
                        opacity: loading || status === 'offline' ? 0.6 : 1
                    }}
                >
                    <Database size={16} /> Sync Whole App To Contacts
                </button>
            </div>
            <div className="status-indicator">
                {status === 'online' ? <CheckCircle size={16} className="status-text online"/> : <XCircle size={16} className="status-text offline"/>}
                Backend: <span className={`status-text ${status}`}>{status.toUpperCase()}</span>
            </div>
            {message && <div className="message-box">{message}</div>}
            {syncAddedItems.length > 0 && (
                <div className="message-box" style={{ marginTop: '12px', maxHeight: '280px', overflowY: 'auto' }}>
                    <div style={{ fontWeight: 700, marginBottom: '8px' }}>Added During Sync</div>
                    <ul style={{ margin: 0, paddingLeft: '18px' }}>
                        {syncAddedItems.map((item, index) => (
                            <li key={`${item.sourceType}-${item.sourceId}-${index}`} style={{ marginBottom: '8px', lineHeight: 1.4 }}>
                                <strong>{item.name || item.label || 'Unnamed contact'}</strong> | {item.action === 'created_contact' ? 'new contact' : 'new source attached'} | {item.sourceType}
                                {Array.isArray(item.details) && item.details.length > 0 ? ` | ${item.details.join(', ')}` : ''}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
            <div className="controls-grid">
                <FileUploadControl 
                    type="ETN" 
                    files={etnFiles} 
                    setFiles={setEtnFiles} 
                    serverFiles={etnServerFiles} 
                    fileView={etnFileView} 
                    setFileView={setEtnFileView} 
                    onUpload={() => handleUpload('etn')} 
                    onRunImport={() => runScript('etn')} 
                    onSchedule={() => openScheduleModal('etn')} 
                    onRefresh={refreshData} 
                    onDelete={(fname) => handleDeleteFile('etn', fname)} 
                    loading={loading} 
                    disabled={status === 'offline'} 
                    fmtDate={fmtDate}
                    lastRunStatus={
                        <div className="stat-box">
                            <div className="stat-box-title">ETN Import</div>
                            <div className="stat-box-info"><Clock size={12}/> Last Run: {fmtDate(jobs['importETN']?.last_run)}</div>
                        </div>
                    }
                />
                <FileUploadControl 
                    type="ZK" 
                    files={zkFiles} 
                    setFiles={setZkFiles} 
                    serverFiles={zkServerFiles} 
                    fileView={zkFileView} 
                    setFileView={setZkFileView} 
                    onUpload={() => handleUpload('zk')} 
                    onRunImport={() => runScript('zk')} 
                    onSchedule={() => openScheduleModal('zk')} 
                    onRefresh={refreshData} 
                    onDelete={(fname) => handleDeleteFile('zk', fname)} 
                    loading={loading} 
                    disabled={status === 'offline'} 
                    fmtDate={fmtDate} 
                    onFolderSelect={handleFolderSelectForZK} 
                    lastRunStatus={
                        <div className="stat-box">
                            <div className="stat-box-title">ZK Import</div>
                            <div className="stat-box-info"><Clock size={12}/> Last Run: {fmtDate(jobs['importZK']?.last_run)}</div>
                            {jobs['importZK']?.details?.new_items !== undefined && (
                                <div className="stat-box-details"><PlusCircle size={12}/> New Items: {jobs['importZK'].details.new_items}</div>
                            )}
                        </div>
                    }
                />
            </div>
            
            {/* Clear ZK Database Button */}
            <div style={{ 
                marginTop: '20px', 
                padding: '20px', 
                backgroundColor: '#fef2f2', 
                border: '2px solid #fecaca',
                borderRadius: '12px'
            }}>
                <h4 style={{ color: '#dc2626', marginBottom: '10px', fontSize: '16px', fontWeight: '600' }}>
                    ⚠️ Danger Zone
                </h4>
                <p style={{ color: '#7f1d1d', fontSize: '14px', marginBottom: '15px' }}>
                    Clear all ZK entries from the database. This action cannot be undone.
                </p>
                <button
                    onClick={handleClearZK}
                    disabled={status === 'offline' || loading}
                    style={{
                        padding: '10px 20px',
                        backgroundColor: '#dc2626',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: status === 'offline' || loading ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        fontWeight: '600',
                        opacity: status === 'offline' || loading ? 0.5 : 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                    }}
                >
                    <Trash2 size={16} />
                    Clear All ZK Entries
                </button>
            </div>

            <ScheduledJobsList jobs={scheduledJobs} onCancel={handleCancelJob} disabled={loading} fmtDate={fmtDate} />
            <div className="controls-grid">
                <FileUploadControl 
                    type="KN" 
                    files={knFiles} 
                    setFiles={setKnFiles} 
                    serverFiles={knServerFiles} 
                    fileView={knFileView} 
                    setFileView={setKnFileView} 
                    onUpload={() => handleUpload('kn')} 
                    onRunImport={() => {}} 
                    onRunImportKn={(tbl) => runScript('kn', tbl)} 
                    onSchedule={() => {}} 
                    onRefresh={refreshData} 
                    onDelete={(fname) => handleDeleteFile('kn', fname)} 
                    loading={loading} 
                    disabled={status === 'offline'} 
                    fmtDate={fmtDate}
                    lastRunStatus={
                        <div className="stat-box">
                            <div className="stat-box-title">KN Import</div>
                            <div className="stat-box-info"><Clock size={12}/> Deli stavb: {fmtDate(jobs['importKN_delistavb']?.last_run)}</div>
                            <div className="stat-box-info"><Clock size={12}/> Imetniki: {fmtDate(jobs['importKN_imetniki']?.last_run)}</div>
                            <div className="stat-box-info"><Clock size={12}/> Osebe: {fmtDate(jobs['importKN_osebe']?.last_run)}</div>
                                <div className="stat-box-info"><Clock size={12}/> Naslovi HS: {fmtDate(jobs['importKN_naslovi']?.last_run)}</div>
                        </div>
                    }
                />
                <FileUploadControl 
                    type="EV" 
                    files={evFiles} 
                    setFiles={setEvFiles} 
                    serverFiles={evServerFiles} 
                    fileView={evFileView} 
                    setFileView={setEvFileView} 
                    onUpload={() => handleUpload('ev')} 
                    onRunImport={() => runScript('ev')} 
                    onSchedule={() => openScheduleModal('ev')} 
                    onRefresh={refreshData} 
                    onDelete={(fname) => handleDeleteFile('ev', fname)} 
                    loading={loading} 
                    disabled={status === 'offline'} 
                    fmtDate={fmtDate}
                    lastRunStatus={
                        <div className="stat-box">
                            <div className="stat-box-title">EV Import</div>
                            <div className="stat-box-info"><Clock size={12}/> Last Run: {fmtDate(jobs['importEV']?.last_run)}</div>
                        </div>
                    }
                />
            </div>
            <div className="controls-grid">
                <FileUploadControl 
                    type="KN STAVBE" 
                    files={knStavbeFiles} 
                    setFiles={setKnStavbeFiles} 
                    serverFiles={knStavbeServerFiles} 
                    fileView={knStavbeFileView} 
                    setFileView={setKnStavbeFileView} 
                    onUpload={() => handleUpload('knstavbe')} 
                    onRunImport={() => runScript('knstavbe')} 
                    onSchedule={() => {}} 
                    showSchedule={false}
                    onRefresh={refreshData} 
                    onDelete={(fname) => handleDeleteFile('knstavbe', fname)} 
                    loading={loading} 
                    disabled={status === 'offline'} 
                    fmtDate={fmtDate}
                    lastRunStatus={
                        <div className="stat-box">
                            <div className="stat-box-title">KN Stavbe Import</div>
                            <div className="stat-box-info"><Clock size={12}/> Last Run: {fmtDate(jobs['importKN_stavbe']?.last_run)}</div>
                        </div>
                    }
                />
            </div>
            <LogViewer />
            <ScheduleModal isOpen={isScheduleModalOpen} onClose={closeScheduleModal} onSchedule={handleScheduleImport} type={scheduleType} scheduleTime={scheduleTime} setScheduleTime={setScheduleTime} loading={loading} />
        </div>
    );
};


export default AdminDashboard;